import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSlidersH } from 'react-icons/fa';
import { ORDER_ALL, SUMMARY_LANGUAGES, orderFor, savePreferences } from '../utils/preferences';
import './DisplayPreferences.css';

// « Affichage »: the signed-in person's own display preferences — the view
// the dashboard opens on, the order of each status, the language of ticket
// summaries. Saved on each change; employees have theirs.
const DisplayPreferences = ({ views = [], statuses = [], preferences, getViewId, onSaved }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(null); // null | 'saving' | 'saved' | 'error'
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const save = async (update) => {
    setState('saving');
    try {
      const saved = await savePreferences(update);
      setState('saved');
      if (onSaved) onSaved(saved, update);
    } catch (error) {
      console.error('Display preferences not saved:', error);
      setState('error');
    }
  };

  const prefs = preferences || { default_view_id: null, ticket_order: {}, summary_language: null };
  const viewIds = views.map((view) => String(getViewId(view))).filter(Boolean);
  const defaultView = prefs.default_view_id && viewIds.includes(String(prefs.default_view_id))
    ? String(prefs.default_view_id) : '';
  const rows = [{ value: ORDER_ALL, label: t('displayPrefs.allStatuses', 'All statuses') }, ...statuses];

  return (
    <div className="display-prefs" ref={rootRef}>
      <button
        type="button"
        className={`display-prefs-trigger${open ? ' open' : ''}`}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        title={t('displayPrefs.title', 'Display preferences')}
      >
        <FaSlidersH aria-hidden="true" />
        <span>{t('displayPrefs.button', 'Display')}</span>
      </button>
      {open && (
        <div className="display-prefs-panel" role="dialog" aria-label={t('displayPrefs.title', 'Display preferences')}>
          <div className="display-prefs-head">
            <span className="display-prefs-heading">{t('displayPrefs.title', 'Display preferences')}</span>
            <span className={`display-prefs-state ${state || ''}`} role="status">
              {state === 'saving' && t('displayPrefs.saving', 'Saving…')}
              {state === 'saved' && t('displayPrefs.saved', 'Saved')}
              {state === 'error' && t('displayPrefs.error', 'Not saved — try again')}
            </span>
          </div>
          <p className="display-prefs-note">{t('displayPrefs.personal', 'These settings are yours: your colleagues keep theirs.')}</p>

          <label className="display-prefs-field">
            <span className="display-prefs-label">{t('displayPrefs.defaultView', 'Default view')}</span>
            <select value={defaultView} onChange={(event) => save({ default_view_id: event.target.value || null })}>
              <option value="">{t('general', 'Général')}</option>
              {views.map((view) => {
                const id = getViewId(view);
                return id ? <option key={id} value={String(id)}>{view.name}</option> : null;
              })}
            </select>
          </label>

          <div className="display-prefs-field">
            <span className="display-prefs-label">{t('displayPrefs.order', 'Display order')}</span>
            <div className="display-prefs-orders">
              {rows.map(({ value, label }) => {
                const current = orderFor(prefs, value);
                const own = Boolean(prefs.ticket_order && prefs.ticket_order[value]);
                return (
                  <div key={value} className={`display-prefs-order${value === ORDER_ALL ? ' all' : ''}`}>
                    <span className={`display-prefs-order-label${own || value === ORDER_ALL ? '' : ' inherited'}`}>{label}</span>
                    <div className="display-prefs-segmented" role="group" aria-label={label}>
                      {['desc', 'asc'].map((order) => (
                        <button
                          key={order}
                          type="button"
                          className={current === order ? 'active' : ''}
                          aria-pressed={current === order}
                          title={order === 'desc'
                            ? t('displayPrefs.newestHint', 'Most recently updated first')
                            : t('displayPrefs.oldestHint', 'Oldest first')}
                          onClick={() => { if (!own || current !== order) save({ ticket_order: { [value]: order } }); }}
                        >
                          {order === 'desc' ? t('displayPrefs.newest', 'Newest') : t('displayPrefs.oldest', 'Oldest')}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <label className="display-prefs-field">
            <span className="display-prefs-label">{t('displayPrefs.summaryLanguage', 'Language of ticket summaries')}</span>
            <select
              value={prefs.summary_language || ''}
              onChange={(event) => event.target.value && save({ summary_language: event.target.value })}
            >
              {!prefs.summary_language && <option value="">{t('displayPrefs.languageDefault', 'Default (French)')}</option>}
              {SUMMARY_LANGUAGES.map((code) => (
                <option key={code} value={code}>{t(`displayPrefs.language_${code}`, code.toUpperCase())}</option>
              ))}
            </select>
          </label>
        </div>
      )}
    </div>
  );
};

export default DisplayPreferences;
